import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { authenticatedFetch } from '../services/authService';

// Keep libraries array stable across renders to avoid LoadScript reload warnings
const GMAPS_LIBRARIES = ['marker'] as const;

interface MapWidgetProps {
  selectedRegion: number | null;
  hideHeader?: boolean;
  externalLoading?: boolean;
  onReady?: () => void;
}

interface WezelItem {
  id: number;
  nazwa: string;
  region_id: number;
  latitude?: number | null;
  longitude?: number | null;
  lat?: number | null;
  lon?: number | null;
}

const WARSAW_COORDS = { lat: 52.2297, lon: 21.0122 };
const WEZEL_ICON_PX = 36;

const MapWidget: React.FC<MapWidgetProps> = ({
  selectedRegion,
  hideHeader = false,
  externalLoading = false,
  onReady
}) => {
  const [wezels, setWezels] = useState<WezelItem[]>([]);
  const [wezelMarkers, setWezelMarkers] = useState<Array<{ id: number; nazwa: string; lat: number; lon: number }>>([]);
  const [coords, setCoords] = useState<{ lat: number; lon: number }>(WARSAW_COORDS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapRef = useRef<any | null>(null);
  const advWezelMarkersRef = useRef<any[]>([]);
  const [mapReadyTick, setMapReadyTick] = useState(0);

  // Read API key
  const readApiKey = (): string => {
    try {
      const w: any = (typeof window !== 'undefined') ? (window as any) : undefined;
      const fromGlobal = w?.__GMAPS_LOADER_KEY__;
      const meta = (typeof document !== 'undefined') ? (document.querySelector('meta[name="gmaps-api-key"]') as HTMLMetaElement | null) : null;
      const fromWindow = w?.GMAPS_API_KEY;
      const fromMeta = meta?.content;
      const fromStorage = (typeof window !== 'undefined') ? localStorage.getItem('GMAPS_API_KEY') : null;
      const k = (fromGlobal || fromWindow || fromMeta || fromStorage || '').toString();
      return k;
    } catch {
      return '';
    }
  };

  // apiKey as state - fetched asynchronously if not available
  const [apiKey, setApiKey] = useState<string>(() => readApiKey());
  const [apiKeyLoading, setApiKeyLoading] = useState<boolean>(() => !readApiKey());

  useEffect(() => {
    const current = readApiKey();
    console.log('[MapWidget] Initial apiKey from meta/window:', current ? '(present)' : '(empty)');
    if (current) {
      setApiKey(current);
      setApiKeyLoading(false);
      return;
    }
    // Fetch from API if not available
    setApiKeyLoading(true);
    let cancelled = false;
    (async () => {
      try {
        console.log('[MapWidget] Fetching apiKey from /api/geocode/key...');
        const res = await authenticatedFetch('/api/geocode/key', { credentials: 'include' });
        if (cancelled) return;
        console.log('[MapWidget] /api/geocode/key response status:', res.status);
        if (res.ok) {
          const j = await res.json();
          console.log('[MapWidget] /api/geocode/key response body:', j?.key ? '(key present)' : '(no key)');
          const key = j && typeof j.key === 'string' ? j.key : '';
          if (!key) {
            console.warn('[MapWidget] apiKey is empty in response');
            setApiKeyLoading(false);
            return;
          }
          console.log('[MapWidget] Setting apiKey');
          // Store globally for other components
          try { (window as any).GMAPS_API_KEY = key; } catch {}
          try { localStorage.setItem('GMAPS_API_KEY', key); } catch {}
          try {
            const meta = (document.querySelector('meta[name="gmaps-api-key"]') as HTMLMetaElement | null);
            if (!meta && typeof document !== 'undefined') {
              const newMeta = document.createElement('meta');
              newMeta.setAttribute('name', 'gmaps-api-key');
              newMeta.setAttribute('content', key);
              document.head.appendChild(newMeta);
            } else if (meta) {
              meta.setAttribute('content', key);
            }
          } catch {}
          setApiKey(key);
        } else {
          console.error('[MapWidget] Failed to fetch apiKey, status:', res.status);
        }
      } catch (e) {
        console.error('[MapWidget] Error fetching apiKey:', e);
      } finally {
        if (!cancelled) {
          setApiKeyLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Read Map ID from meta/window
  const readMapId = (): string => {
    try {
      const m = (document.querySelector('meta[name="gmaps-map-id"]') as HTMLMetaElement | null)?.content || (window as any)?.GMAPS_MAP_ID || '';
      return String(m || '');
    } catch { return ''; }
  };

  // mapId as state - fetched asynchronously if not available from meta/window
  const [mapId, setMapId] = useState<string>(() => readMapId());
  const [mapIdLoading, setMapIdLoading] = useState<boolean>(() => !readMapId());

  useEffect(() => {
    const current = readMapId();
    console.log('[MapWidget] Initial mapId from meta/window:', current || '(empty)');
    if (current) {
      setMapId(current);
      setMapIdLoading(false);
      return;
    }
    // Fetch from API if not available
    setMapIdLoading(true);
    let cancelled = false;
    (async () => {
      try {
        console.log('[MapWidget] Fetching mapId from /api/geocode/map-id...');
        const res = await authenticatedFetch('/api/geocode/map-id', { credentials: 'include' });
        if (cancelled) return;
        console.log('[MapWidget] /api/geocode/map-id response status:', res.status);
        if (res.ok) {
          const j = await res.json();
          console.log('[MapWidget] /api/geocode/map-id response body:', j);
          const id = j && typeof j.mapId === 'string' ? j.mapId : '';
          if (!id) {
            console.warn('[MapWidget] mapId is empty in response');
            setMapIdLoading(false);
            return;
          }
          console.log('[MapWidget] Setting mapId:', id.slice(0, 10) + '...');
          // Store globally for other components
          try { (window as any).GMAPS_MAP_ID = id; } catch {}
          try {
            const meta = (document.querySelector('meta[name="gmaps-map-id"]') as HTMLMetaElement | null);
            if (!meta && typeof document !== 'undefined') {
              const newMeta = document.createElement('meta');
              newMeta.setAttribute('name', 'gmaps-map-id');
              newMeta.setAttribute('content', id);
              document.head.appendChild(newMeta);
            } else if (meta) {
              meta.setAttribute('content', id);
            }
          } catch {}
          setMapId(id);
        } else {
          console.error('[MapWidget] Failed to fetch mapId, status:', res.status);
        }
      } catch (e) {
        console.error('[MapWidget] Error fetching mapId:', e);
      } finally {
        if (!cancelled) {
          setMapIdLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const readyNotifiedRef = useRef(false);
  const notifyReady = useCallback(() => {
    if (readyNotifiedRef.current) return;
    readyNotifiedRef.current = true;
    onReady?.();
  }, [onReady]);

  // Load wezels for selected region
  useEffect(() => {
    let aborted = false;
    readyNotifiedRef.current = false;

    const loadWezels = async () => {
      if (!selectedRegion) {
        setWezels([]);
        setWezelMarkers([]);
        setCoords(WARSAW_COORDS);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const resp = await authenticatedFetch(`/api/wezels?region_id=${selectedRegion}`, { credentials: 'include' });
        if (!resp.ok) {
          throw new Error('Failed to load wezels');
        }
        const data = await resp.json();
        const wezelsList: WezelItem[] = data.wezels || [];
        
        if (aborted) return;
        setWezels(wezelsList);

        // Build markers for wezels with valid coordinates
        const markers: Array<{ id: number; nazwa: string; lat: number; lon: number }> = [];
        for (const w of wezelsList) {
          const rawLat = (w.latitude ?? w.lat) as any;
          const rawLon = (w.longitude ?? w.lon) as any;
          const lat = rawLat !== undefined && rawLat !== null ? parseFloat(String(rawLat)) : NaN;
          const lon = rawLon !== undefined && rawLon !== null ? parseFloat(String(rawLon)) : NaN;
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            markers.push({ id: w.id, nazwa: w.nazwa, lat, lon });
          }
        }

        if (aborted) return;
        setWezelMarkers(markers);

        // Set center to first wezel or Warsaw
        if (markers.length > 0) {
          setCoords({ lat: markers[0].lat, lon: markers[0].lon });
        } else {
          setCoords(WARSAW_COORDS);
        }
      } catch (e: any) {
        if (!aborted) {
          setError(e?.message || 'Błąd pobierania węzłów');
          notifyReady();
        }
      } finally {
        if (!aborted) {
          setLoading(false);
        }
      }
    };

    loadWezels();

    return () => { aborted = true; };
  }, [selectedRegion]);

  useEffect(() => {
    if (!loading && !apiKeyLoading && !mapIdLoading && (error || !apiKey || !apiKey.trim() || !mapId || !mapId.trim())) {
      notifyReady();
    }
  }, [loading, apiKeyLoading, mapIdLoading, error, apiKey, mapId, notifyReady]);

  useEffect(() => {
    if (!loading && !error && mapRef.current) {
      notifyReady();
    }
  }, [loading, error, notifyReady]);

  // Fit bounds to show all markers
  const fitBounds = (map: any, markers: { lat: number; lon: number }[], centerCoords: { lat: number; lon: number }) => {
    const g = (typeof window !== 'undefined' ? (window as any).google : undefined);
    if (!map || !g?.maps) return;
    if (markers && markers.length > 0) {
      const b = new g.maps.LatLngBounds();
      markers.forEach(m => b.extend(new g.maps.LatLng(m.lat, m.lon)));
      map.fitBounds(b);
    } else {
      // Default view around center
      const radiusKm = 4;
      const dLat = radiusKm / 111;
      const dLon = radiusKm / (111 * Math.cos((centerCoords.lat * Math.PI) / 180) || 1e-6);
      const sw = new g.maps.LatLng(centerCoords.lat - dLat, centerCoords.lon - dLon);
      const ne = new g.maps.LatLng(centerCoords.lat + dLat, centerCoords.lon + dLon);
      const bounds = new g.maps.LatLngBounds(sw, ne);
      map.fitBounds(bounds);
    }
  };

  // Create AdvancedMarkerElement markers - REQUIRED, no fallback
  useEffect(() => {
    const g: any = (typeof window !== 'undefined') ? (window as any).google : undefined;
    const map = mapRef.current;
    if (!g || !map) return;

    // Validate requirements
    const markerLib = g?.maps?.marker;
    const hasValidMapId = typeof mapId === 'string' && mapId.trim().length > 0;
    const hasAdvancedMarker = !!(markerLib && markerLib.AdvancedMarkerElement);

    if (!hasValidMapId) {
      setError('Brak Map ID – wymagane dla AdvancedMarkerElement');
      return;
    }

    if (!hasAdvancedMarker) {
      setError('AdvancedMarkerElement nie jest dostępny – sprawdź konfigurację Google Maps API');
      return;
    }

    // Clear existing markers
    try {
      advWezelMarkersRef.current.forEach(m => {
        try { m.map = null; } catch {}
      });
      advWezelMarkersRef.current = [];
    } catch {}

    // Create AdvancedMarkerElement markers for wezels
    for (const m of wezelMarkers) {
      try {
        const img = document.createElement('img');
        img.src = '/red_pin_point.png';
        img.style.width = `${WEZEL_ICON_PX}px`;
        img.style.height = `${WEZEL_ICON_PX}px`;
        img.style.transform = 'translate(-50%, -100%)';
        const adv = new markerLib.AdvancedMarkerElement({
          map,
          position: { lat: m.lat, lng: m.lon },
          title: m.nazwa,
          content: img
        });
        advWezelMarkersRef.current.push(adv);
      } catch (err) {
        console.error('[MapWidget] Error creating AdvancedMarkerElement:', err);
      }
    }

    // Fit bounds to show all markers
    if (wezelMarkers.length > 0) {
      fitBounds(map, wezelMarkers.map(m => ({ lat: m.lat, lon: m.lon })), coords);
    }

    return () => {
      try {
        advWezelMarkersRef.current.forEach(m => {
          try { m.map = null; } catch {}
        });
        advWezelMarkersRef.current = [];
      } catch {}
    };
  }, [wezelMarkers, mapReadyTick, mapId, coords.lat, coords.lon]);

  // MapCanvas component
  const MapCanvas: React.FC<{
    apiKey: string;
    mapId: string;
    coords: { lat: number; lon: number };
    markers: Array<{ lat: number; lon: number }>;
    onLoad: (map: any) => void;
    fitBoundsFn: (map: any, markers: Array<{ lat: number; lon: number }>, centerCoords: { lat: number; lon: number }) => void;
    onReady?: () => void;
  }> = ({ apiKey, mapId, coords, markers, onLoad, fitBoundsFn, onReady }) => {
    const { isLoaded, loadError } = useJsApiLoader({
      id: 'google-map-script',
      googleMapsApiKey: apiKey || '',
      version: 'weekly',
      libraries: GMAPS_LIBRARIES as any
    });

    useEffect(() => {
      if (loadError) {
        onReady?.();
      }
    }, [loadError, onReady]);

    if (loadError) {
      return (
        <div style={{ fontSize: '11px', color: '#d32f2f', padding: '8px' }}>
          Błąd ładowania Google Maps
        </div>
      );
    }

    if (!isLoaded) {
      return (
        <div style={{ fontSize: '11px', color: '#757575', padding: '8px' }}>
          Ładowanie Google Maps...
        </div>
      );
    }

    // Validate mapId is required
    if (!mapId || !mapId.trim()) {
      onReady?.();
      return (
        <div style={{ fontSize: '11px', color: '#d32f2f', padding: '8px' }}>
          Błąd: Map ID jest wymagane dla AdvancedMarkerElement
        </div>
      );
    }

    return (
      <GoogleMap
        onLoad={(map) => {
          onLoad(map);
          if (markers.length > 0) {
            fitBoundsFn(map, markers, coords);
          } else {
            fitBoundsFn(map, [], coords);
          }
          try { setMapReadyTick(t => t + 1); } catch {}
          onReady?.();
        }}
        onUnmount={() => {}}
        mapContainerStyle={{ height: '100%', width: '100%', minHeight: '200px' }}
        center={{ lat: coords.lat, lng: coords.lon }}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          mapId: mapId.trim()
        }}
      >
        {/* Advanced markers managed imperatively */}
      </GoogleMap>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        border: hideHeader ? 'none' : '1px solid var(--border)',
        borderRadius: hideHeader ? '0' : '4px',
        background: 'var(--card)',
        minWidth: 0,
        overflow: 'hidden',
        minHeight: hideHeader ? '160px' : 'auto',
        height: hideHeader ? '160px' : 'auto'
      }}
    >
      {!hideHeader && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 10px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg)'
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Mapa</span>
          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
            {wezelMarkers.length > 0 ? `${wezelMarkers.length} węzłów` : 'Brak węzłów'}
          </span>
        </div>
      )}
      <div style={{ padding: hideHeader ? '0' : '8px', display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minHeight: 0 }}>
        {(loading || apiKeyLoading || mapIdLoading) && (
          <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Ładowanie konfiguracji mapy...</div>
        )}
        {!loading && !apiKeyLoading && !mapIdLoading && error && (
          <div style={{ fontSize: '11px', color: 'var(--error)' }}>{error}</div>
        )}
        {!loading && !apiKeyLoading && !mapIdLoading && !error && (!apiKey || !apiKey.trim()) && (
          <div style={{ fontSize: '11px', color: 'var(--error)', padding: '8px' }}>
            Błąd: Brak GMAPS_API_KEY – nie można załadować Map Google
          </div>
        )}
        {!loading && !apiKeyLoading && !mapIdLoading && !error && apiKey && apiKey.trim() && (!mapId || !mapId.trim()) && (
          <div style={{ fontSize: '11px', color: 'var(--error)', padding: '8px' }}>
            Błąd: Brak Map ID – wymagane dla AdvancedMarkerElement
          </div>
        )}
        {!loading && !apiKeyLoading && !mapIdLoading && !error && apiKey && apiKey.trim() && mapId && mapId.trim() && (
          <div style={{ flex: 1, minHeight: 0, position: 'relative', height: hideHeader ? '160px' : '200px' }}>
            <MapCanvas
              apiKey={apiKey}
              mapId={mapId}
              coords={coords}
              markers={wezelMarkers.map(m => ({ lat: m.lat, lon: m.lon }))}
              onLoad={(map) => {
                mapRef.current = map;
              }}
              fitBoundsFn={fitBounds}
              onReady={notifyReady}
            />
            {(externalLoading || loading) && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.65)',
                  fontSize: '11px',
                  color: 'var(--muted)',
                  pointerEvents: 'none'
                }}
              >
                Ładowanie danych...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapWidget;

